import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import ReactCalendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { CalendarDays, X, Loader2 } from "lucide-react";
import { axiosInstance } from "../../App";
import { getLocalDateString } from "../../lib/utils";

/**
 * Calendar component with dot indicators for dates that have data
 * Uses React Portal to render modal outside of parent container
 */
export default function CalendarWithDots({ 
  dataType, 
  selectedDate, 
  onDateChange,
  dotColor = "bg-pink-500",
  user
}) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [datesWithData, setDatesWithData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Memoize calendar value to prevent unnecessary re-renders
  const calendarValue = useMemo(() => {
    return typeof selectedDate === 'string' 
      ? new Date(selectedDate + 'T00:00:00') 
      : selectedDate;
  }, [selectedDate]);

  // Fetch dates that have data for the current month view
  const fetchDatesWithData = useCallback(async (year, month) => {
    setIsLoading(true);
    try {
      const startDate = getLocalDateString(new Date(year, month, 1));
      const endDate = getLocalDateString(new Date(year, month + 1, 0));
      
      let endpoint = '';
      switch (dataType) {
        case 'activities':
          endpoint = `/activities/dates?start_date=${startDate}&end_date=${endDate}`;
          break;
        case 'daily-logs':
          endpoint = `/daily-logs/dates?start_date=${startDate}&end_date=${endDate}`;
          break;
        case 'nutrition':
          endpoint = `/nutrition/dates?start_date=${startDate}&end_date=${endDate}`;
          break;
        default:
          setIsLoading(false);
          return;
      }
      
      const response = await axiosInstance.get(endpoint);
      setDatesWithData(response.data.dates || []);
    } catch (error) {
      console.log(`Dates endpoint not available for ${dataType}`);
      setDatesWithData([]);
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
  }, [dataType]);

  // Fetch data when calendar opens or month changes
  useEffect(() => {
    if (showCalendar) {
      fetchDatesWithData(currentMonth.getFullYear(), currentMonth.getMonth());
    }
  }, [showCalendar, currentMonth, fetchDatesWithData]);

  // Reset hasFetched when calendar closes
  useEffect(() => {
    if (!showCalendar) {
      setHasFetched(false);
    }
  }, [showCalendar]);

  const handleDateChange = useCallback((date) => {
    const dateStr = getLocalDateString(date);
    onDateChange(dateStr);
    setShowCalendar(false);
  }, [onDateChange]);

  const handleActiveStartDateChange = useCallback(({ activeStartDate }) => {
    setCurrentMonth(activeStartDate);
  }, []);

  const handleClose = useCallback(() => {
    setShowCalendar(false);
  }, []);

  const handleOpen = useCallback(() => {
    setShowCalendar(true);
  }, []);

  // Check if a date has data - memoized for performance
  const hasDataOnDate = useCallback((date) => {
    const dateStr = getLocalDateString(date);
    return datesWithData.includes(dateStr);
  }, [datesWithData]);

  // Calendar Modal Component - rendered via Portal
  const CalendarModal = useMemo(() => {
    if (!showCalendar) return null;
    
    return (
      <div 
        className="fixed inset-0 bg-black/40 flex items-center justify-center p-4"
        style={{ zIndex: 99999 }}
        onClick={handleClose}
      >
        <div 
          className="bg-white rounded-3xl shadow-2xl p-5 w-full max-w-[340px] relative"
          style={{ animation: 'fadeIn 0.15s ease-out' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>

          {/* Calendar Title */}
          <h3 className="text-center font-bold text-gray-800 mb-4 text-base">
            📅 Pilih Tarikh
          </h3>

          {/* Loading indicator */}
          {isLoading && !hasFetched && (
            <div className="absolute inset-0 bg-white/80 rounded-3xl flex items-center justify-center z-10">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          )}

          <ReactCalendar
            onChange={handleDateChange}
            value={calendarValue}
            onActiveStartDateChange={handleActiveStartDateChange}
            tileContent={({ date, view }) => {
              if (view === 'month' && hasDataOnDate(date)) {
                return (
                  <div className={`w-2 h-2 ${dotColor} rounded-full mx-auto mt-1`}></div>
                );
              }
              return null;
            }}
            className="calendar-modal-style"
            locale="ms-MY"
          />

          {/* Legend */}
          <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-center gap-2 text-xs text-gray-500">
            <div className={`w-2.5 h-2.5 ${dotColor} rounded-full`}></div>
            <span>Ada rekod pada tarikh ini</span>
          </div>
        </div>

        {/* Styles */}
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.98); }
            to { opacity: 1; transform: scale(1); }
          }
          .calendar-modal-style {
            width: 100% !important;
            border: none !important;
            background: transparent !important;
            font-family: inherit;
          }
          .calendar-modal-style .react-calendar__navigation {
            margin-bottom: 10px;
          }
          .calendar-modal-style .react-calendar__navigation button {
            font-weight: 700;
            font-size: 1rem;
            color: #374151;
            min-width: 40px;
            background: none;
            border-radius: 10px;
          }
          .calendar-modal-style .react-calendar__navigation button:hover {
            background: #f3e8ff;
          }
          .calendar-modal-style .react-calendar__navigation button:disabled {
            background: none;
            color: #9ca3af;
          }
          .calendar-modal-style .react-calendar__month-view__weekdays {
            font-weight: 700;
            color: #9333ea;
            text-transform: uppercase;
            font-size: 0.7rem;
          }
          .calendar-modal-style .react-calendar__month-view__weekdays__weekday abbr {
            text-decoration: none;
          }
          .calendar-modal-style .react-calendar__tile {
            border-radius: 12px;
            padding: 10px 6px;
            font-weight: 600;
            font-size: 0.9rem;
            position: relative;
          }
          .calendar-modal-style .react-calendar__tile:hover {
            background: linear-gradient(135deg, #f3e8ff 0%, #fce7f3 100%);
          }
          .calendar-modal-style .react-calendar__tile--active {
            background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%) !important;
            color: white !important;
          }
          .calendar-modal-style .react-calendar__tile--now {
            background: #fdf4ff;
            font-weight: 800;
          }
          .calendar-modal-style .react-calendar__tile--now:not(.react-calendar__tile--active) {
            color: #9333ea;
          }
          .calendar-modal-style .react-calendar__month-view__days__day--weekend {
            color: #dc2626;
          }
          .calendar-modal-style .react-calendar__month-view__days__day--neighboringMonth {
            color: #d1d5db;
          }
        `}</style>
      </div>
    );
  }, [showCalendar, isLoading, hasFetched, calendarValue, handleDateChange, handleActiveStartDateChange, handleClose, hasDataOnDate, dotColor]);

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={handleOpen}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl font-bold text-sm transition-all ${
          showCalendar 
            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' 
            : 'bg-white border-2 border-purple-200 text-purple-600 hover:border-purple-400'
        }`}
        data-testid="calendar-toggle-btn"
      >
        <CalendarDays className="w-4 h-4" />
        Kalendar
      </button>

      {/* Calendar Modal - Rendered via Portal to document.body */}
      {showCalendar && createPortal(CalendarModal, document.body)}
    </>
  );
}
